import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  TouchableOpacity, View, Text, TextInput,
  ImageBackground, ScrollView, Modal
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInventoryReports, updateReportToArchived,
  deleteInventoryReport, showInventoryReport,
  exportInventoryReport,
  createInventoryReport,
  fetchArchivedInventoryReports
} from '../../actions/inventoryReportActions';
import { fontFamily } from '../../helpers/fontFamily';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import CommonStyles from '../../style/commonStyles';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { showMessage } from "react-native-flash-message";
import Pagination from '../inventoryReports/pagination';
import DeleteConfirmationModal from '../inventoryReports/deleteConfirmationModal';
import InventoryReportTable from '../inventoryReports/inventoryReportTable';
import { clearSavedProductData } from '../../actions/productAction';
import { useLayoutEffect } from 'react';

const inventoryReport = () => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const route = useRoute();
  const mode = route.params?.mode || '';
  const [activeTab, setActiveTab] = useState( mode === 'receive' ? 'Receiving Records' : 'Recounting Records' );
  const [recordName, setRecordName] = useState('');
  const [recordNumber, setRecordNumber] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const inputRef = useRef(null);
  const inventoryReports = useSelector(state => state.inventoryReports.reports);
  const archivedReports = useSelector(state => state.inventoryReports.archivedReports);
  const allReports = [...inventoryReports, ...archivedReports];

  const pagy = useSelector(state => state.inventoryReports.pagy);
  const [showError, setShowError] = useState(false);
  const nonArchivedReports = inventoryReports.filter(report => report && !report.archived &&
    report.name && typeof report.name === 'string' && report.name.trim() !== '');
  const hasUserTyped = useRef(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (allReports.length > 0 && !hasUserTyped.current) {
        const sortedReports = [...allReports].sort((a, b) => {
          if (a.created_at && b.created_at) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return b.id - a.id;
        });

        const latestReport = sortedReports[0];
        if (latestReport && latestReport.name) {
          const nameParts = latestReport.name.split('#');
          const baseName = nameParts[0].trim();
          handleRecordNameChange(baseName, true);
        }
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [allReports]);

  useFocusEffect(
    useCallback(() => {
      const fetchReports = async () => {
        try {
          dispatch(fetchArchivedInventoryReports(currentPage, mode));
          dispatch(fetchInventoryReports(currentPage, mode));
          await AsyncStorage.removeItem('current_report_id');
          await AsyncStorage.removeItem('current_report');
          await AsyncStorage.removeItem('current_report_mode');
        } catch (error) {
          console.error('Failed to fetch inventory reports or remove mode:', error);
        }
      };

      fetchReports();
    }, [dispatch, currentPage, mode])
  );

  useEffect(() => {
    if (recordName.trim()) {
      handleRecordNameChange(recordName);
    }
  }, [inventoryReports, archivedReports]);

  useEffect(() => {
    dispatch(clearSavedProductData());
  }, [dispatch]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const markAsArchived = (id) => {
    dispatch(updateReportToArchived(id))
    .then((message) => {
      showMessage({
        message: message || "Report Marked as Archvvied successfully!",
        type: "success",
        backgroundColor: '#056EAA',
        duration: 5000
      });
      setTimeout(() => {
        dispatch(fetchInventoryReports(currentPage, mode));
        dispatch(fetchArchivedInventoryReports(currentPage, mode));
      }, 1000);
    })
    .catch(() => {
      showMessage({
        message: "Failed to restore report.",
        type: "danger",
      });
    });
  };

  const confirmDelete = (id) => {
    setSelectedReportId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = () => {
    if (selectedReportId) {
      dispatch(deleteInventoryReport(selectedReportId))
        .then((message) => {
          showMessage({
            message: message || "Report deleted successfully!",
            type: "success",
          });
        })
        .catch(() => {
          showMessage({
            message: "Failed to delete report.",
            type: "danger",
          });
        });
      setShowDeleteModal(false);
      setSelectedReportId(null);
    }
  };

  const showReport = async (report) => {
    try {
      await AsyncStorage.setItem('current_report_mode', mode);
      await AsyncStorage.setItem('current_report', JSON.stringify(report));
      navigation.navigate('ViewInventoryReport', { id: report.id, view: 'show', from: 'index', mode: mode });
    } catch (error) {
      console.error('Error storing mode:', error);
    }
  };

  const resumeReport = async (report, mode) => {
    const combinedName = report.name.trim();

    try {
      await AsyncStorage.setItem('current_report_id', report.id.toString());
      await AsyncStorage.setItem('current_report', JSON.stringify(report));
      await AsyncStorage.setItem('current_report_mode', mode);
    } catch (error) {
      console.error('Error storing reportId:', error);
    }

    if (mode === 'receive') {
      navigation.navigate('ScanReceive', {
        combinedName,
        reportId: report.id,
      });
    } else {
      navigation.navigate('ScanRecount', {
        combinedName,
        reportId: report.id,
      });
    }
  };

  const exportReport = (id) => {
    dispatch(exportInventoryReport(id, mode))
      .then((message) => {
        showMessage({
          message: message || "Export email sent successfully!",
          type: "success",
          backgroundColor: '#056EAA',
          duration: 5000
        });
      })
      .catch(() => {
        showMessage({
          message: "Failed to send export email.",
          type: "danger",
        });
      });
  };

  const handleViewAndPush = async (report) => {
    try {
      if (report && report.id && report.name) {
        await AsyncStorage.setItem('current_report', JSON.stringify(report));
        await AsyncStorage.setItem('current_report_mode', mode);
      }
    } catch (error) {
      console.error('Error saving report info for PushInventory:', error);
    }
    navigation.navigate('PushInventory', {
      mode: mode,
      reportId: report.id,
      timestamp: new Date().getTime()
    });
  };

  const handleStartReceiving = async () => {
    const trimmedName = recordName.trim();
    if (!trimmedName) {
      setShowError(true);
      return;
    }
    setShowError(false);
    const combinedName = `${trimmedName} #${recordNumber.trim()}`.trim();

    try {
      const report = dispatch(createInventoryReport({
        name: combinedName,
        store_id: await AsyncStorage.getItem('inventory_store_id'),
        report_type: mode
      })).then(report => {
        AsyncStorage.setItem('current_report_id', report.id.toString())
        AsyncStorage.setItem('current_report', JSON.stringify(report));
        AsyncStorage.setItem('current_report_mode', mode);
      });

      if (mode === 'receive') {
        navigation.navigate('ScanReceive', {
          combinedName,
          reportId: report.id,
        });
      } else {
        navigation.navigate('ScanRecount', {
          combinedName,
          reportId: report.id,
        });
      }
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to start receiving.');
    }
  };

  const handleInputFocus = () => {
    setShowError(false);
  };

  const handleTabChange = (tab) => setActiveTab(tab);

  const renderTabButton = (label) => (
    <TouchableOpacity
      onPress={() => handleTabChange(label)}
      style={{
        paddingTop: "3px",
        paddingBottom: "3px",
        paddingLeft: "10px",
        paddingRight: "10px",
        backgroundColor: activeTab === label ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.3)',
        marginRight: 10,
        borderBottomLeftRadius: '0px',
        borderBottomEndRadius: '0px',
        borderTopRightRadius: '5px',
        borderTopLeftRadius: '5px',
      }}
    >
      <Text style={{
        color: activeTab === label ? '#ffffff' : '#999999',
        fontFamily: fontFamily.font300,
        fontSize: 16
      }}>{label}</Text>
    </TouchableOpacity>
  );

  const renderReports = () => {
    if (nonArchivedReports.length === 0) {
      return (
        <Text style={{ textAlign: 'center', marginTop: 20, marginBottom: 20, color: '#aaa', fontFamily: fontFamily.font300, fontSize: 16 }}>
          No reports found
        </Text>
      );
    }

    return nonArchivedReports.map(report => (
      <InventoryReportTable
        key={report.id}
        report={report}
        onExport={exportReport}
        onView={showReport}
        onResume={resumeReport}
        onDelete={confirmDelete}
        onArchive={markAsArchived}
        onViewAndPush={handleViewAndPush}
        mode={mode}
      />
    ));
  };

  const handleRecordNameChange = (text, skipUserTypedFlag = false) => {
    if (!skipUserTypedFlag) {
      hasUserTyped.current = true;
    }
    setRecordName(text);
    const trimmedText = text.trim();
    if (!trimmedText) return setRecordNumber('001');

    const existingRecords = allReports.filter(report => {
      if (!report.name || typeof report.name !== 'string') return false;
      const baseName = report.name.split('#')[0].trim().toLowerCase();
      return baseName === trimmedText.toLowerCase();
    });

    if (existingRecords.length > 0) {
      const numbers = existingRecords.map(report => {
        if (!report.name || typeof report.name !== 'string') return 0;
        const match = report.name.match(/#(\d+)$/);
        if (match) {
          const num = parseInt(match[1]);
          return num;
        }
        return 0;
      }).filter(num => num >= 0);

      if (numbers.length > 0) {
        const maxNumber = Math.max(...numbers);
        const nextNumber = maxNumber + 1;
        const formattedNumber = nextNumber.toString().padStart(3, '0');
        setRecordNumber(formattedNumber);
      } else {
        setRecordNumber('001');
      }
    } else {
      setRecordNumber('001');
    }
  };

  return (
    <LinearGradient
      colors={[
        '#0A1118',
        '#111C29',
        '#253C57',
        '#5C778F',
        '#5C778F',
        '#253C57',
        '#111B27'
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      locations={[0.09, 0.19, 0.43, 0.58, 0.78, 0.92]}
      style={{flex: 1}}
    >
    <DeleteConfirmationModal
      visible={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDeleteConfirmed}
    />

    <ScrollView contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>

      <View style={{
        width: '100%',
        maxWidth: 800,
        alignSelf: 'center',
        paddingHorizontal: 20,
        marginTop: 20,
        marginBottom: 30
      }}>
        <View style={{ 
          flexDirection: 'row',
          width: '100%',
          gap: 10,
          marginBottom: 15,
          alignItems: 'baseline'
        }}>
          <View style={{ flex: 1 }}>
            <TextInput
              placeholder={
                showError
                  ? "Required"
                  : mode === 'receive'
                    ? "Receiving Record"
                    : "Recounting Record"
              }
              placeholderTextColor={showError ? '#ff0000' : '#999999'}
              value={recordName}

              onChangeText={(text) => handleRecordNameChange(text, false)}
              onFocus={handleInputFocus}
              style={[
                CommonStyles.receiveScanInput,
                showError && {
                  borderColor: '#ff0000',
                  color: '#393838',
                },
                { width: '100%', height: 29 }
              ]}
              ref={inputRef}
              autoFocus={true}
              onSubmitEditing={handleStartReceiving}
            />
          </View>
          <View style={{ width: 100 }}>
            <TextInput
              placeholder="#001"
              value={recordNumber ? `#${recordNumber}` : ''}
              onChangeText={setRecordNumber}
              onSubmitEditing={handleStartReceiving}
              editable={false}
              style={{
                width: '100%',
                borderRadius: 10,
                paddingHorizontal: 15,
                fontFamily: fontFamily.font300,
                backgroundColor: '#dbdada',
                height: 29,
                fontSize: 16,
                color: '#393838',
              }}
            />
          </View>
        </View>

        <TouchableOpacity
          style={{
            backgroundColor: '#4BDA9C',
            borderRadius: 15,
            width: 146,
            height: 29,
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 10,
            alignSelf: 'center'
          }}
          onPress={handleStartReceiving}
        >
          <Text style={{
            color: '#000000',
            fontSize: 16,
            fontFamily: fontFamily.font300,
          }}>
            {mode === 'receive' ? 'Start Receiving' : 'Start Recounting'}
          </Text>
        </TouchableOpacity>

        <Text style={{
          textAlign: 'center',
          fontSize: 14,
          color: '#dddddd',
          fontFamily: fontFamily.font300,
          marginBottom: 10,
        }}>
          To create a new record give your record a optional name and number then tap start to begin receiving items on this record.
        </Text>
      </View>

      <View style={{
        minHeight: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        width: '100%'
      }}>
        <View style={{
          padding: '20px',
          paddingBottom: '0px',
          width: '100%',
          maxWidth: 800,
          margin: 'auto',
          marginBottom: '1px'
        }}>
          <View style={{ flexDirection: 'row', marginLeft: '-5px' }}>
            {/* {renderTabButton('Receiving Records')} */}
            {renderTabButton(mode === 'receive' ? 'Receiving Records' : 'Recounting Records')}
            {renderTabButton('Purchase Orders')}
          </View>
        </View>


        {activeTab === (mode === 'receive' ? 'Receiving Records' : 'Recounting Records') && (
          <>
            <LinearGradient
              colors={['#e6dddd17', '#ffffff66']}
              start = { {x: 0.5, y: 1 }}
              end   = { {x: 0.5, y: 0 }}
              style={{
                padding: 15,
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
                marginTop: -3,
                width: '100%',
                maxWidth: 800,
                alignSelf: 'center',
                zIndex: 1,
              }}
            >
            </LinearGradient>

              <View
                style={{
                  flexGrow: 1,
                  width: '100%',
                  maxWidth: 800,
                  alignSelf: 'center',
                  paddingHorizontal: 10,
                }}
              >
                {renderReports()}
              </View>

            <LinearGradient
              colors={['rgba(0, 0, 0, 0.2)', 'rgba(255, 255, 255, 0.15)']}
              start={{ x: 1, y: 0 }}
              end={{ x: 0, y: 0 }}
              style={{
                padding: 15,
                borderBottomLeftRadius: 18,
                borderBottomRightRadius: 18,
                width: '100%',
                maxWidth: 800,
                alignSelf: 'center',
              }}
            >
            </LinearGradient>
          </>
        )}
      </View>

      {activeTab === (mode === 'receive' ? 'Receiving Records' : 'Recounting Records') &&(
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pagy={pagy}
        />
      )}

      {activeTab === 'Purchase Orders' && (
        <>
          <LinearGradient
              colors={['rgba(0, 0, 0, 0.6)', 'rgba(255, 255, 255, 0.15)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{
                padding: 15,
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
                marginTop: -3,
                width: '100%',
                maxWidth: 800,
                alignSelf: 'center',
                zIndex: 1,
              }}
            >
          </LinearGradient>
          <Text style={{
            color: '#aaa',
            fontSize: 16,
            fontFamily: fontFamily.font300,
            marginTop: 20,
            marginBottom: 20,
            textAlign: 'center'
          }}>
            Purchase Orders is coming soon.
          </Text>
          <LinearGradient
            colors={['rgba(0, 0, 0, 0.2)', 'rgba(255, 255, 255, 0.15)']}
            start={{ x: 1, y: 0 }}
            end={{ x: 0, y: 0 }}
            style={{
              padding: 15,
              borderBottomLeftRadius: 18,
              borderBottomRightRadius: 18,
              width: '100%',
              maxWidth: 800,
              alignSelf: 'center',
            }}
          >
          </LinearGradient>
        </>
      )}

        {activeTab === (mode === 'receive' ? 'Receiving Records' : 'Recounting Records') && (
          <TouchableOpacity
            style={{
              borderWidth: 1,
              borderColor: '#ccc',
              borderRadius: 10,
              paddingVertical: 10,
              paddingHorizontal: 25,
              marginTop: 20,
              marginBottom: 20,
              alignSelf: 'center'
            }}
            onPress={() => navigation.navigate('ArchivedReceivingReports', { mode: mode })}
          >
            <Text style={{ color: '#ffffff', fontFamily: fontFamily.font300 }}>Show Archived</Text>
          </TouchableOpacity>
      )}
    </ScrollView>
  </LinearGradient>
  );
};

export default inventoryReport;
